import { useEffect, useMemo, useState } from "react";
import { CalendarDays, FileText, Loader2, MapPin, Scale } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useCreateMatter, useDeals, useMatters } from "@/hooks/use-supabase-queries";
import { formatNaira } from "@/lib/format";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface CreateMatterDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreatedMatter: (matterId: string) => void;
}

type FormState = {
  dealId: string;
  title: string;
  propertyAddress: string;
  propertyValue: string;
  estimatedCompletion: string;
};

const initialFormState: FormState = {
  dealId: "",
  title: "",
  propertyAddress: "",
  propertyValue: "",
  estimatedCompletion: "",
};

export function CreateMatterDialog({
  open,
  onOpenChange,
  onCreatedMatter,
}: CreateMatterDialogProps) {
  const { user } = useAuth();
  const { data: deals, isLoading: isLoadingDeals } = useDeals();
  const { data: matters, isLoading: isLoadingMatters } = useMatters();
  const createMatter = useCreateMatter();
  const [form, setForm] = useState<FormState>(initialFormState);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});

  const eligibleDeals = useMemo(() => {
    const matterDealIds = new Set((matters || []).map((matter) => matter.deal_id));

    return (deals || []).filter((deal) => {
      const isAssignedToLawyer = !deal.lawyer_id || deal.lawyer_id === user?.id;
      const canCreateMatterFromStatus = deal.status === "accepted" || deal.status === "in-progress";
      return isAssignedToLawyer && canCreateMatterFromStatus && !matterDealIds.has(deal.id);
    });
  }, [deals, matters, user?.id]);

  const selectedDeal = eligibleDeals.find((deal) => deal.id === form.dealId) || null;

  useEffect(() => {
    if (!open) {
      setForm(initialFormState);
      setErrors({});
      return;
    }

    if (!form.dealId && eligibleDeals.length > 0) {
      const fallbackDeal = eligibleDeals[0];
      setForm({
        dealId: fallbackDeal.id,
        title: fallbackDeal.property_title,
        propertyAddress: fallbackDeal.property_address,
        propertyValue: fallbackDeal.estimated_value ? String(fallbackDeal.estimated_value) : "",
        estimatedCompletion: "",
      });
    }
  }, [eligibleDeals, form.dealId, open]);

  const setField = (field: keyof FormState, value: string) => {
    setForm((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: undefined }));
  };

  const handleDealChange = (dealId: string) => {
    const deal = eligibleDeals.find((currentDeal) => currentDeal.id === dealId);
    if (!deal) return;

    setForm({
      dealId,
      title: deal.property_title,
      propertyAddress: deal.property_address,
      propertyValue: deal.estimated_value ? String(deal.estimated_value) : "",
      estimatedCompletion: form.estimatedCompletion,
    });
    setErrors({});
  };

  const validate = () => {
    const nextErrors: Partial<Record<keyof FormState, string>> = {};

    if (!form.dealId) nextErrors.dealId = "Select a source deal.";
    if (!form.title.trim()) nextErrors.title = "Matter title is required.";
    if (!form.propertyAddress.trim()) nextErrors.propertyAddress = "Property address is required.";
    if (form.propertyValue && Number(form.propertyValue) < 0) nextErrors.propertyValue = "Property value cannot be negative.";

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!user || !selectedDeal || !validate()) return;

    createMatter.mutate(
      {
        deal_id: selectedDeal.id,
        lawyer_id: user.id,
        client_id: selectedDeal.client_id || null,
        agent_id: selectedDeal.agent_id || null,
        title: form.title.trim(),
        property_address: form.propertyAddress.trim(),
        property_value: form.propertyValue ? Number(form.propertyValue) : null,
        estimated_completion: form.estimatedCompletion || null,
      },
      {
        onSuccess: (matter) => {
          toast.success(`Matter ${matter.matter_number} created successfully.`);
          onOpenChange(false);
          onCreatedMatter(matter.id);
        },
        onError: (error) => {
          toast.error("Could not create matter", {
            description: error.message,
          });
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Scale className="h-5 w-5 text-primary" />
            Create Matter
          </DialogTitle>
          <DialogDescription>
            Start a matter from an accepted deal that has not yet been converted into an active legal file.
          </DialogDescription>
        </DialogHeader>

        {isLoadingDeals || isLoadingMatters ? (
          <div className="flex min-h-48 items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : eligibleDeals.length === 0 ? (
          <div className="space-y-4 rounded-2xl border border-dashed border-border bg-muted/30 p-6 text-center">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10">
              <FileText className="h-5 w-5 text-primary" />
            </div>
            <div className="space-y-2">
              <h3 className="text-lg font-semibold text-foreground">No eligible deals available</h3>
              <p className="text-sm leading-6 text-muted-foreground">
                Review and accept a deal intake first, or return when an accepted deal needs to be converted into a matter.
              </p>
            </div>
            <DialogFooter className="sm:justify-center">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Close
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="rounded-[1.5rem] border border-border bg-muted/30 p-5">
              <div className="grid gap-5 sm:grid-cols-2">
                <div className="space-y-2 sm:col-span-2">
                  <Label>Source Deal</Label>
                  <Select value={form.dealId} onValueChange={handleDealChange}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select an accepted deal" />
                    </SelectTrigger>
                    <SelectContent>
                      {eligibleDeals.map((deal) => (
                        <SelectItem key={deal.id} value={deal.id}>
                          {deal.deal_number} - {deal.property_title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.dealId && <p className="text-sm text-destructive">{errors.dealId}</p>}
                </div>

                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="matter-title">Matter Title</Label>
                  <Input
                    id="matter-title"
                    value={form.title}
                    onChange={(event) => setField("title", event.target.value)}
                    placeholder="Luxury Duplex Purchase - Ikoyi"
                  />
                  {errors.title && <p className="text-sm text-destructive">{errors.title}</p>}
                </div>

                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="matter-address">Property Address</Label>
                  <Input
                    id="matter-address"
                    value={form.propertyAddress}
                    onChange={(event) => setField("propertyAddress", event.target.value)}
                    placeholder="Lekki Phase 1, Lagos"
                  />
                  {errors.propertyAddress && <p className="text-sm text-destructive">{errors.propertyAddress}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="matter-value">Property Value</Label>
                  <Input
                    id="matter-value"
                    type="number"
                    min="0"
                    value={form.propertyValue}
                    onChange={(event) => setField("propertyValue", event.target.value)}
                    placeholder="250000000"
                  />
                  {errors.propertyValue && <p className="text-sm text-destructive">{errors.propertyValue}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="matter-estimated-completion">Estimated Completion</Label>
                  <Input
                    id="matter-estimated-completion"
                    type="date"
                    value={form.estimatedCompletion}
                    onChange={(event) => setField("estimatedCompletion", event.target.value)}
                  />
                </div>
              </div>
            </div>

            {selectedDeal && (
              <div className="grid gap-4 rounded-[1.5rem] border border-border bg-card p-5 sm:grid-cols-3">
                <div className="rounded-2xl bg-muted/40 p-4">
                  <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Deal Number</p>
                  <p className="mt-2 text-sm font-semibold text-foreground">{selectedDeal.deal_number}</p>
                </div>
                <div className="rounded-2xl bg-muted/40 p-4">
                  <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Property Value</p>
                  <p className="mt-2 text-sm font-semibold text-foreground">
                    {selectedDeal.estimated_value ? formatNaira(selectedDeal.estimated_value) : "Not provided"}
                  </p>
                </div>
                <div className="rounded-2xl bg-muted/40 p-4">
                  <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Buyer</p>
                  <p className="mt-2 text-sm font-semibold text-foreground">{selectedDeal.buyer_name}</p>
                </div>
                <div className="rounded-2xl bg-muted/40 p-4 sm:col-span-2">
                  <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Address</p>
                  <p className="mt-2 flex items-center gap-2 text-sm font-semibold text-foreground">
                    <MapPin className="h-4 w-4 text-primary" />
                    {selectedDeal.property_address}
                  </p>
                </div>
                <div className="rounded-2xl bg-muted/40 p-4">
                  <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Accepted</p>
                  <p className="mt-2 flex items-center gap-2 text-sm font-semibold text-foreground">
                    <CalendarDays className="h-4 w-4 text-primary" />
                    {selectedDeal.accepted_at ? new Date(selectedDeal.accepted_at).toLocaleDateString() : "Recently"}
                  </p>
                </div>
              </div>
            )}

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={createMatter.isPending}>
                Cancel
              </Button>
              <Button onClick={handleSubmit} disabled={createMatter.isPending || !selectedDeal}>
                {createMatter.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating Matter...
                  </>
                ) : (
                  "Create Matter"
                )}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
